import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/ascend/AppShell";
import MoodSpark from "@/components/ascend/MoodSpark";
import AffirmationCard from "@/components/ascend/AffirmationCard";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Lock, Eye, EyeOff, ChevronLeft, ChevronRight, Save, Heart } from "lucide-react";
import { addDays, isoDate, parseIsoDate } from "@/lib/time";
import { Link } from "react-router-dom";

const MOOD_EMOJI = { 1: "😔", 2: "😐", 3: "🙂", 4: "😊", 5: "🌟" };

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthOffset, setMonthOffset] = useState(0); // 0 = current month
  const [selectedDate, setSelectedDate] = useState(null);
  const [revealed, setRevealed] = useState({}); // {date: true}
  const [privateEdit, setPrivateEdit] = useState(""); // buffer for current selectedDate
  const [gratitudeEdit, setGratitudeEdit] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [list, s] = await Promise.all([
          api.get("/checkins?limit=90"),
          api.get("/journal/stats?days=30"),
        ]);
        setEntries(list.data.items || []);
        setStats(s.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const entriesByDate = useMemo(() => {
    const m = new Map();
    entries.forEach((e) => m.set(e.date, e));
    return m;
  }, [entries]);

  const selectedEntry = selectedDate ? entriesByDate.get(selectedDate) : null;

  useEffect(() => {
    setPrivateEdit(selectedEntry?.private_note || "");
    setGratitudeEdit(selectedEntry?.gratitude_text || "");
  }, [selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePrivateNote = async () => {
    if (!selectedEntry) return;
    try {
      const { data } = await api.patch(`/checkins/${selectedEntry.date}`, { private_note: privateEdit });
      setEntries((arr) => arr.map((e) => (e.date === data.date ? data : e)));
      toast.success("Saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveGratitude = async () => {
    if (!selectedEntry) return;
    try {
      const { data } = await api.patch(`/checkins/${selectedEntry.date}`, { gratitude_text: gratitudeEdit });
      setEntries((arr) => arr.map((e) => (e.date === data.date ? data : e)));
      toast.success("Saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Calendar rendering
  const today = new Date();
  const viewMonthDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const monthLabel = viewMonthDate.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const daysInMonth = new Date(viewMonthDate.getFullYear(), viewMonthDate.getMonth() + 1, 0).getDate();
  const firstDow = (viewMonthDate.getDay() + 6) % 7; // Mon=0
  const grid = [];
  for (let i = 0; i < firstDow; i++) grid.push(null);
  for (let d = 1; d <= daysInMonth; d++) grid.push(new Date(viewMonthDate.getFullYear(), viewMonthDate.getMonth(), d));

  return (
    <AppShell>
      <Toaster richColors position="top-center" />
      <div className="pt-2">
        <div className="mb-6 fade-up flex items-start justify-between gap-4">
          <div>
            <span className="eyebrow">your quiet ledger</span>
            <h1 className="font-display text-4xl sm:text-5xl mt-1" style={{ color: "var(--text)" }}>Journal</h1>
            <p className="text-muted mt-1">Every check-in you've written lives here.</p>
          </div>
          <Link
            to="/gratitude"
            className="btn-ghost text-xs flex items-center gap-1.5 flex-shrink-0"
            data-testid="journal-gratitude-wall-link"
            title="Open the Gratitude Wall"
          >
            <Heart size={14} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
            <span className="hidden sm:inline">Gratitude Wall</span>
            <span className="sm:hidden">Wall</span>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: Calendar + Sparkline */}
          <section className="lg:col-span-5 space-y-5">
            <div className="card-soft p-5 fade-up" data-testid="mood-30day">
              <div className="flex items-baseline justify-between mb-2">
                <span className="eyebrow">last 30 days</span>
                <span className="font-hand text-base" style={{ color: "var(--accent)" }}>
                  avg {stats?.avg_mood ?? "—"}
                </span>
              </div>
              <MoodSpark series={stats?.series || []} height={56} />
            </div>

            <div className="card-soft p-5 fade-up delay-1" data-testid="journal-calendar">
              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={() => setMonthOffset((n) => n - 1)}
                  className="btn-ghost !px-2 !py-1"
                  data-testid="calendar-prev-btn"
                  aria-label="Previous month"
                >
                  <ChevronLeft size={14} strokeWidth={1.5} />
                </button>
                <div className="font-display text-xl" style={{ color: "var(--text)" }}>{monthLabel}</div>
                <button
                  onClick={() => setMonthOffset((n) => Math.min(0, n + 1))}
                  className="btn-ghost !px-2 !py-1 disabled:opacity-40"
                  disabled={monthOffset === 0}
                  data-testid="calendar-next-btn"
                  aria-label="Next month"
                >
                  <ChevronRight size={14} strokeWidth={1.5} />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-wider text-muted mb-1">
                {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <div key={i}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {grid.map((d, i) => {
                  if (!d) return <div key={i} />;
                  const iso = isoDate(d);
                  const entry = entriesByDate.get(iso);
                  const isFuture = d > today;
                  const isToday = iso === isoDate(today);
                  const isSelected = iso === selectedDate;
                  return (
                    <button
                      key={i}
                      onClick={() => !isFuture && setSelectedDate(iso)}
                      disabled={isFuture}
                      data-testid={`calendar-day-${iso}`}
                      className="aspect-square rounded-xl flex flex-col items-center justify-center text-xs transition-all"
                      style={{
                        background: isSelected ? "var(--primary)" : entry ? "color-mix(in oklab, var(--primary-soft) 55%, transparent)" : "transparent",
                        border: `1px solid ${isToday ? "var(--primary)" : "transparent"}`,
                        color: isSelected ? "#fff" : "var(--text)",
                        opacity: isFuture ? 0.3 : 1,
                      }}
                    >
                      <span className="font-medium leading-none">{d.getDate()}</span>
                      {entry?.mood != null && (
                        <span className="text-base leading-none mt-0.5">{MOOD_EMOJI[entry.mood]}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Right: Selected entry / recent list */}
          <section className="lg:col-span-7 space-y-5" data-testid="journal-detail">
            {selectedEntry ? (
              <div className="card-soft p-6 fade-up">
                <div className="flex items-baseline justify-between mb-3">
                  <div>
                    <span className="eyebrow">{selectedDate}</span>
                    <div className="font-display text-3xl mt-1" style={{ color: "var(--text)" }}>
                      {MOOD_EMOJI[selectedEntry.mood] || "—"} <span className="font-hand text-2xl ml-2" style={{ color: "var(--accent)" }}>{describeMood(selectedEntry.mood)}</span>
                    </div>
                  </div>
                  {selectedEntry.locked && <span className="text-[11px] px-2 py-1 rounded-full flex items-center gap-1" style={{ background: "var(--surface-2)", color: "var(--text-muted)", border: "1px solid var(--border)" }}>
                    <Lock size={11} strokeWidth={1.5} /> locked
                  </span>}
                </div>

                {selectedEntry.affirmation_shown && (
                  <div className="mb-4">
                    <AffirmationCard text={selectedEntry.affirmation_shown} source={selectedEntry.affirmation_source} size="sm" testid="journal-detail-affirmation" />
                  </div>
                )}

                {selectedEntry.win_text && (
                  <div className="mb-4">
                    <div className="eyebrow mb-1">win</div>
                    <div className="font-display text-lg italic leading-snug" style={{ color: "var(--text)" }}>
                      "{selectedEntry.win_text}"
                    </div>
                  </div>
                )}

                {/* Gratitude — private, always editable */}
                <div className="mb-4" data-testid="journal-detail-gratitude">
                  <div className="flex items-center gap-2 mb-1">
                    <Heart size={12} strokeWidth={1.75} style={{ color: "var(--accent)" }} />
                    <span className="eyebrow">grateful for</span>
                    <span className="text-[10px] italic text-muted">just for you</span>
                  </div>
                  <textarea
                    className="input-soft"
                    value={gratitudeEdit}
                    onChange={(e) => setGratitudeEdit(e.target.value)}
                    rows={3}
                    style={{
                      minHeight: 84,
                      background: "color-mix(in oklab, var(--primary-soft) 22%, var(--surface))",
                      fontFamily: "var(--font-hand, inherit)",
                    }}
                    data-testid="gratitude-edit-input"
                    placeholder="Nothing here yet."
                    maxLength={1200}
                  />
                  {(gratitudeEdit || "") !== (selectedEntry.gratitude_text || "") && (
                    <div className="flex justify-end mt-2">
                      <button onClick={saveGratitude} className="btn-primary text-sm flex items-center gap-2" data-testid="gratitude-save-btn">
                        <Save size={14} strokeWidth={1.5} /> Save
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="eyebrow">private note</span>
                    <button
                      onClick={() => setRevealed((r) => ({ ...r, [selectedEntry.date]: !r[selectedEntry.date] }))}
                      className="text-xs flex items-center gap-1"
                      style={{ color: "var(--primary)" }}
                      data-testid="private-reveal-toggle"
                    >
                      {revealed[selectedEntry.date] ? <><EyeOff size={12} strokeWidth={1.5} /> hide</> : <><Eye size={12} strokeWidth={1.5} /> reveal</>}
                    </button>
                  </div>
                  {revealed[selectedEntry.date] ? (
                    <div>
                      <textarea
                        className="input-soft"
                        value={privateEdit}
                        onChange={(e) => setPrivateEdit(e.target.value)}
                        rows={5}
                        style={{ minHeight: 120, background: "color-mix(in oklab, var(--primary-soft) 15%, var(--surface))" }}
                        data-testid="private-edit-input"
                        placeholder="Nothing here yet."
                      />
                      <div className="flex justify-end mt-2">
                        <button onClick={savePrivateNote} className="btn-primary text-sm flex items-center gap-2" data-testid="private-save-btn">
                          <Save size={14} strokeWidth={1.5} /> Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl px-4 py-6 text-center text-sm text-muted" style={{ background: "color-mix(in oklab, var(--primary-soft) 15%, var(--surface))", border: "1px dashed var(--border)" }}>
                      hidden — tap "reveal" to read
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="card-soft p-6 fade-up">
                <span className="eyebrow">recent</span>
                <h3 className="font-display text-2xl mt-1 mb-4" style={{ color: "var(--text)" }}>Last few days</h3>
                {loading && <div className="text-muted text-sm">Loading…</div>}
                {!loading && entries.length === 0 && (
                  <div className="text-center py-10 font-hand text-lg text-muted">
                    nothing to look back on yet — write your first check-in ✎
                  </div>
                )}
                <ul className="space-y-2">
                  {entries.slice(0, 7).map((e) => (
                    <li key={e.date}>
                      <button
                        onClick={() => setSelectedDate(e.date)}
                        className="w-full card-soft-2 p-3 text-left flex items-center gap-3 hover:opacity-90"
                        data-testid={`journal-recent-${e.date}`}
                      >
                        <span className="text-2xl">{MOOD_EMOJI[e.mood] || "·"}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium" style={{ color: "var(--text)" }}>{e.date}</div>
                          {e.win_text && <div className="text-xs text-muted truncate italic">"{e.win_text}"</div>}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </div>
      </div>
    </AppShell>
  );
}

function describeMood(m) {
  return { 1: "heavy day", 2: "meh day", 3: "okay day", 4: "good day", 5: "shining day" }[m] || "";
}
